import { useGoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import { useAuth } from 'context/auth';
import { Formik, FormikValues } from 'formik';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';

import { Layout } from 'components/layout';

import { InputField, Loader } from 'common';
import { Button } from 'common/new-button';
import { initialValues, model, validationSchema } from './config';

import { handleMutation } from 'utils/handleMutation';

import { BASE_URL, URLS } from 'api/constants';
import axios from 'axios';
import { Icon } from 'common/icon';
import { useAlert } from 'components';
import { CustomMetadata } from 'components/metadata';
import { allMetadata } from 'constants/metadata';
import { Routes } from 'constants/routes';
import { useGoogleAuthMutation, useMagicLinkMutation } from 'hooks/api/auth';
import { useLazyMeQuery } from 'hooks/api/users';

import { Analytics } from 'constants/analytics';
import { getRedirectOptions } from 'utils/redirectOptions';
import styles from './styles.module.css';

type Props = {
  isRegister?: boolean;
};

interface UserSubmitProps extends FormikValues {
  email?: string;
}

const useAuthLogin = () => {
  const alert = useAlert();
  const { setLoggedIn } = useAuth();

  const [fetchUser] = useLazyMeQuery();

  const getUser = useCallback(
    async (token: string) => {
      await axios.get(BASE_URL + URLS.ME, {
        headers: { Authorization: `Token ${token}` },
      });

      alert.success('Success');
      setLoggedIn(token?.toString() ?? '');
      await fetchUser({ isSafe: false });
    },
    [alert, fetchUser, setLoggedIn]
  );

  return { getUser };
};

export function LoginWithOneTap() {
  const alert = useAlert();
  const { getUser } = useAuthLogin();
  const [googleAuth, googleAuthMutation] = useGoogleAuthMutation();

  const onSuccessGoogleAuth = useCallback(
    async (data: any) => {
      await getUser(data?.authToken);
    },
    [getUser]
  );

  const onErrorGoogleAuth = useCallback(
    (error: any) => {
      alert.success('Something went wrong');
      console.error(error?.data?.detail || 'Error');
    },
    [alert]
  );

  handleMutation({
    ...googleAuthMutation,
    onSuccess: onSuccessGoogleAuth,
    onError: onErrorGoogleAuth,
  });

  useGoogleOneTapLogin({
    onSuccess: (credentialResponse) => {
      googleAuth({
        oauthToken: decodeURIComponent(credentialResponse?.credential ?? ''),
      });
    },

    onError: () => {
      // eslint-disable-next-line no-console
      console.log('One Tap Error');
    },
    auto_select: false, // попробовать включить
  });

  return <div id="google-one-tap" />;
}

const GoogleLoginButton = () => {
  const router = useRouter();
  const alert = useAlert();
  const [googleAuth, googleAuthMutation] = useGoogleAuthMutation();

  const onSuccessGoogleAuth = useCallback(
    (data: any) => {
      router.push(`/auth?token=${data?.authToken}`);
    },
    [router]
  );

  const onErrorGoogleAuth = useCallback(
    (error: any) => {
      alert.success('Something went wrong');
      console.error(error?.data?.detail || 'Error');
    },
    [alert]
  );

  handleMutation({
    ...googleAuthMutation,
    onSuccess: onSuccessGoogleAuth,
    onError: onErrorGoogleAuth,
  });

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) =>
      googleAuth({
        oauthToken: tokenResponse?.access_token ?? '',
      }),
  });

  return (
    <Button
      id={Analytics.auth.google}
      variant="secondary"
      icon={<Icon.Google />}
      onClick={() => handleGoogleLogin()}
      text={'Log in with Google'}
    />
  );
};

export const AuthPage = ({ isRegister }: Props) => {
  const router = useRouter();
  const alert = useAlert();

  const [register, mutation] = useMagicLinkMutation();

  const onSuccess = useCallback(() => {
    alert.success('Email has been sent');
  }, [alert]);

  const onError = useCallback(
    (error: any) => {
      alert.success('Something went wrong');
      console.error(error?.data?.detail || 'Error');
    },
    [alert]
  );

  handleMutation({ ...mutation, onSuccess, onError });

  const handleAccount = () => {
    if (isRegister) {
      router.push(Routes.login);
    } else {
      router.push(Routes.register);
    }
  };

  const onSubmit = async (values: UserSubmitProps) => {
    const { email } = values;
    await register({ email });
  };

  return (
    <div>
      <CustomMetadata metadata={allMetadata.login} />
      <Layout isSearchInputMobileVisible>
        <main className={styles.main}>
          <div className={styles.login}>
            <div className={styles.loginWrapper}>
              <div className={styles.loginRow}>
                <Formik
                  onSubmit={onSubmit}
                  initialValues={initialValues}
                  validationSchema={validationSchema()}
                  className={styles.loginForm}
                >
                  {({
                    dirty,
                    isSubmitting,
                    validateForm,
                    submitForm,
                    errors,
                  }) => (
                    <>
                      <div className={styles.loginColumn}>
                        <div className={styles.wrapperField}>
                          <div className={styles.loginFormInput}>
                            <InputField {...model.email} variant="loginField" />
                          </div>
                          {errors.email && (
                            <span className={styles.error}>{errors.email}</span>
                          )}
                        </div>

                        <Button
                          id={Analytics.auth.email}
                          text={
                            (isSubmitting && 'Loading...') ||
                            (isRegister ? 'Sign Up' : 'Sign in')
                          }
                          disabled={isSubmitting || !dirty}
                          onClick={(e) => {
                            e.stopPropagation();
                            validateForm();
                            submitForm();
                          }}
                          variant="primary"
                        />
                        <div className={styles.loginSignUp}>
                          {isRegister
                            ? 'Have an account?'
                            : "Don't have an account?"}
                          <span onClick={handleAccount}>
                            {' '}
                            {isRegister ? 'Sign in' : 'Sign Up'}
                          </span>
                        </div>
                      </div>

                      <div className={styles.loginOr}>
                        <span>{'or'}</span>
                      </div>

                      <div className={styles.loginColumn}>
                        <LoginWithOneTap />
                        <GoogleLoginButton />
                      </div>
                    </>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </main>
      </Layout>
    </div>
  );
};

export const SubmitMagicLink = () => {
  const router = useRouter();
  const alert = useAlert();
  const { getUser } = useAuthLogin();

  const { token } = router.query;

  const handleAuth = useCallback(async () => {
    try {
      await getUser(token as string);
    } catch (error) {
      alert.error('Wrong Auth Link');
    } finally {
      const redirectOptions = getRedirectOptions();
      const { url } = redirectOptions ?? {};
      if (!url) {
        router.push(Routes.home);
      }
    }
  }, [alert, getUser, router, token]);

  useEffect(() => {
    if (token) {
      handleAuth();
    }
  }, [handleAuth, token]);

  return <Loader size="big" />;
};
